/**
 * Authentication API module for communicating with the backend
 */

import type { ConfirmEmailChangeResult } from 'minka-shared';
import type {
    LoginCredentials,
    UpdateProfileData,
    UpdateProfileResponse,
    AuthResponse,
    User
} from '@/types/auth.types';
import config from '@/env-config';
import { formatApiErrorMessage } from '@/utils/api-error';
import { ApiRequestError } from '@/services/invitation-api';
import { getActiveSocketId } from '@/services/socket-session';

// Health check function removed to avoid rate limiting issues

class AuthAPI {
    /**
     * Read through to storage rather than cached: other modules (the invite
     * flow) and other tabs write the token too, and a password reset or change
     * replaces it, so a copy held here would go stale.
     */
    private get token(): string | null {
        return localStorage.getItem('jwt_token');
    }

    /**
     * Login with email and password
     */
    async login(credentials: LoginCredentials): Promise<AuthResponse> {
        try {
            
            const response = await fetch(`${config.api.baseUrl}/auth/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(credentials),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(formatApiErrorMessage(data, `Login failed: ${response.status}`));
            }

            if (data.success && data.data?.token) {
                this.setToken(data.data.token);
            }

            return data;
        } catch (error) {
            console.error('Login error:', error);
            throw error;
        }
    }

    /**
     * "Forgot password?" The answer is the same whether or not the address has
     * an account, so success carries no information beyond "the request was
     * taken"; the only failures are validation and the per-IP rate limit.
     */
    async requestPasswordReset(email: string): Promise<void> {
        const response = await fetch(`${config.api.baseUrl}/auth/forgot-password`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email }),
        });

        if (!response.ok) {
            const data = await response.json().catch(() => ({}));
            throw new ApiRequestError(
                formatApiErrorMessage(data, `Request failed: ${response.status}`),
                response.status,
                typeof data?.code === 'string' ? data.code : undefined
            );
        }
    }

    /**
     * Redeem a reset link. Success signs the user in: the response carries a
     * fresh token, issued after every earlier one was revoked. A dead link
     * fails with code `password_reset_invalid`.
     */
    async resetPassword(token: string, password: string): Promise<AuthResponse> {
        const response = await fetch(`${config.api.baseUrl}/auth/reset-password`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token, password }),
        });

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
            throw new ApiRequestError(
                formatApiErrorMessage(data, `Password reset failed: ${response.status}`),
                response.status,
                typeof data?.code === 'string' ? data.code : undefined
            );
        }

        if (data.success && data.data?.token) {
            this.setToken(data.data.token);
        }

        return data;
    }

    /**
     * Follow the link a self-service email change sent to the new address,
     * with the session of the account that asked: no session (or an expired
     * one) is a 401. A dead link, or a session of another account, fails with
     * code `email_change_invalid`; an address another account took meanwhile
     * with `email_taken`.
     */
    async confirmEmailChange(token: string): Promise<ConfirmEmailChangeResult> {
        const response = await fetch(`${config.api.baseUrl}/auth/confirm-email-change`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(this.token ? { 'Authorization': `Bearer ${this.token}` } : {}),
            },
            body: JSON.stringify({ token }),
        });

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
            throw new ApiRequestError(
                formatApiErrorMessage(data, `Email confirmation failed: ${response.status}`),
                response.status,
                typeof data?.code === 'string' ? data.code : undefined
            );
        }

        return data.data;
    }

    /**
     * Update user profile. A new email comes back in `pendingEmail`, not in
     * `data`: it applies only once the link sent to it is followed.
     */
    async updateProfile(data: UpdateProfileData): Promise<UpdateProfileResponse> {
        try {
            if (!this.token) {
                throw new Error('No authentication token available');
            }

            // Naming this tab's live socket lets a password change sign out
            // every other session but not this one.
            const socketId = getActiveSocketId();
            const response = await fetch(`${config.api.baseUrl}/users/profile`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${this.token}`,
                    'Content-Type': 'application/json',
                    ...(socketId ? { 'X-Socket-Id': socketId } : {}),
                },
                body: JSON.stringify(data),
            });

            const result = await response.json();
            
            if (!response.ok) {
                throw new Error(formatApiErrorMessage(result, `Profile update failed: ${response.status}`));
            }

            // A password change revoked the token this request was sent with;
            // the server hands back its replacement.
            if (result.success && typeof result.token === 'string') {
                this.setToken(result.token);
            }

            return result;
        } catch (error) {
            console.error('Profile update error:', error);
            throw error;
        }
    }

    /**
     * Get current user profile
     */
    async getProfile(): Promise<{ success: boolean; data: User; error?: string }> {
        try {
            if (!this.token) {
                throw new Error('No authentication token available');
            }

            const response = await fetch(`${config.api.baseUrl}/users/profile`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${this.token}`,
                    'Content-Type': 'application/json',
                },
            });

            const data = await response.json();
            
            if (!response.ok) {
                throw new Error(data.error || `Profile fetch failed: ${response.status}`);
            }

            return data;
        } catch (error) {
            console.error('Profile fetch error:', error);
            throw error;
        }
    }

    /**
     * Logout and clear authentication
     */
    logout(): void {
        localStorage.removeItem('jwt_token');
    }

    /**
     * Check if user is authenticated
     */
    isAuthenticated(): boolean {
        return !!this.token;
    }

    /**
     * Check if the current token is valid (not expired)
     */
    isTokenValid(): boolean {
        if (!this.token) return false;
        
        try {
            const payload = JSON.parse(atob(this.token.split('.')[1]));
            const currentTime = Date.now() / 1000;
            return payload.exp > currentTime;
        } catch (error) {
            console.error('Invalid token format:', error);
            return false;
        }
    }

    /**
     * Get the current authentication token
     */
    getToken(): string | null {
        return this.token;
    }

    /**
     * Set the authentication token
     */
    private setToken(token: string): void {
        localStorage.setItem('jwt_token', token);
    }
}

// Create and export a singleton instance
const authAPI = new AuthAPI();
export default authAPI;